/**
 * ECharts options for chat fences and auto-charted tables.
 * Hex comes from chart-theme: canvas cannot read CSS variables.
 */
import { chartTheme } from '../core/chart-theme.js';

function echartsApi() {
  const api = globalThis.echarts;
  return api && typeof api.init === 'function' ? api : null;
}

function paint(root) {
  const theme = chartTheme(root || document.documentElement);
  const signal = theme.signal || '#ff4d12';
  return {
    bg: theme.surface || theme.background || '#121316',
    title: theme.text || '#f2f2ef',
    text: theme.muted || theme.text || '#8c8e94',
    label: theme.text || '#f2f2ef',
    axis: theme.border || '#2e3036',
    pieBorder: theme.surface || theme.background || '#121316',
    tipBg: theme.surface || theme.background || '#121316',
    tipText: theme.text || '#f2f2ef',
    palette: [signal, theme.accent || signal, theme.text, theme.muted].filter(Boolean),
  };
}

function asNumber(value) {
  if (value == null) return null;
  if (typeof value === 'object') return Number(value.value || 0) || 0;
  return Number(value) || 0;
}

function seriesList(payload) {
  if (Array.isArray(payload.series) && payload.series.length) return payload.series;
  if (Array.isArray(payload.data)) return [{ label: payload.label || 'Value', data: payload.data }];
  return [];
}

function pieData(labels, series) {
  const raw = Array.isArray(series[0] && series[0].data) ? series[0].data : [];
  if (!raw.length) return labels.map((label) => ({ name: label, value: 0 }));
  return raw.map((item, i) => (item !== null && typeof item === 'object'
    ? { name: item.name || item.label || labels[i], value: Number(item.value || item.data || 0) || 0 }
    : { name: labels[i] || String(i), value: Number(item) || 0 }));
}

/**
 * @param {string} type
 * @param {object} payload
 * @param {Element} [root]
 */
export function chartOption(type, payload, root) {
  const tone = paint(root);
  const labels = Array.isArray(payload.labels) ? payload.labels : [];
  const series = seriesList(payload);
  const title = payload.title ? {
    text: payload.title,
    left: 'center',
    textStyle: { fontSize: 13, fontWeight: 600, color: tone.title },
  } : null;
  const tooltip = {
    backgroundColor: tone.tipBg,
    borderColor: tone.axis,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: tone.tipText, fontSize: 12 },
  };

  if (type === 'pie' || type === 'doughnut') {
    return {
      title,
      backgroundColor: tone.bg,
      tooltip: { ...tooltip, trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 0, type: 'scroll', textStyle: { color: tone.text, fontSize: 11 } },
      series: [{
        type: 'pie',
        radius: type === 'doughnut' ? ['42%', '68%'] : '68%',
        center: ['50%', '46%'],
        data: pieData(labels, series),
        label: { color: tone.label, fontSize: 11 },
        itemStyle: { borderColor: tone.pieBorder, borderWidth: 2 },
      }],
      color: tone.palette,
    };
  }

  if (type === 'funnel') {
    return {
      title,
      backgroundColor: tone.bg,
      tooltip: { ...tooltip, trigger: 'item', formatter: '{b}: {c}' },
      series: [{
        type: 'funnel',
        left: '10%',
        width: '80%',
        top: title ? 42 : 24,
        bottom: 12,
        minSize: '5%',
        maxSize: '100%',
        sort: 'descending',
        gap: 2,
        label: { show: true, position: 'inside', color: '#121316', fontSize: 11, formatter: '{b}' },
        itemStyle: { borderColor: tone.pieBorder, borderWidth: 1 },
        data: pieData(labels, series),
      }],
      color: tone.palette,
    };
  }

  const horizontal = type === 'hbar';
  const isBar = type === 'bar' || type === 'hbar';
  const category = {
    type: 'category',
    data: labels,
    axisLabel: { color: tone.text, fontSize: 11 },
    axisLine: { lineStyle: { color: tone.axis } },
    axisTick: { show: false },
  };
  const value = {
    type: 'value',
    axisLabel: { color: tone.text, fontSize: 11 },
    splitLine: { lineStyle: { color: tone.axis } },
  };
  const showLabels = labels.length <= 8;
  const chartSeries = series.map((item, i) => {
    const color = tone.palette[i % tone.palette.length];
    const data = (item.data || []).map(asNumber);
    if (isBar) {
      return {
        name: item.label || item.name || `Series ${i + 1}`,
        type: 'bar',
        data,
        barMaxWidth: 34,
        itemStyle: { color, borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] },
        label: showLabels
          ? { show: true, position: horizontal ? 'right' : 'top', color: tone.label, fontSize: 10 }
          : { show: false },
      };
    }
    return {
      name: item.label || item.name || `Series ${i + 1}`,
      type: 'line',
      data,
      smooth: true,
      symbolSize: 6,
      lineStyle: { width: 2.5, color },
      itemStyle: { color },
      areaStyle: series.length === 1 ? { color, opacity: 0.1 } : undefined,
    };
  });
  return {
    title,
    backgroundColor: tone.bg,
    grid: { left: 8, right: 24, top: title ? 42 : 26, bottom: 8, containLabel: true },
    tooltip: { ...tooltip, trigger: 'axis', axisPointer: { type: horizontal ? 'shadow' : 'line' } },
    xAxis: horizontal ? value : category,
    yAxis: horizontal ? category : value,
    series: chartSeries,
    color: tone.palette,
  };
}

let watching = false;

function watchTheme() {
  if (watching || typeof MutationObserver === 'undefined') return;
  watching = true;
  new MutationObserver(() => {
    document.querySelectorAll('.msg-chart[data-chart-json]').forEach((node) => mountOne(node));
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  window.addEventListener('resize', () => {
    const api = echartsApi();
    if (!api) return;
    document.querySelectorAll('.msg-chart').forEach((node) => {
      const inst = api.getInstanceByDom(node);
      if (inst) inst.resize();
    });
  });
}

function mountOne(node) {
  const api = echartsApi();
  if (!api) return;
  try {
    const type = node.getAttribute('data-chart-type') || 'bar';
    const payload = JSON.parse(node.getAttribute('data-chart-json') || '{}');
    if (!payload || !Object.keys(payload).length) return;
    const existing = api.getInstanceByDom(node);
    if (existing) existing.dispose();
    const inst = api.init(node);
    inst.setOption(chartOption(type, payload, node));
  } catch (err) {
    console.error('[markdown] chart', err && err.message);
  }
}

/** @param {ParentNode} root */
export function mountCharts(root, left = 8) {
  if (!root || !root.querySelectorAll) return;
  watchTheme();
  const nodes = [...root.querySelectorAll('.msg-chart[data-chart-json]')];
  if (!nodes.length) return;
  if (nodes.some((node) => node.clientWidth < 2)) {
    if (left > 0) requestAnimationFrame(() => mountCharts(root, left - 1));
    return;
  }
  nodes.forEach(mountOne);
}

/** Tear down ECharts instances under root (dialog close). */
export function disposeCharts(root) {
  if (!root || !root.querySelectorAll) return;
  const api = echartsApi();
  if (!api) return;
  root.querySelectorAll('.msg-chart').forEach((node) => {
    const inst = api.getInstanceByDom(node);
    if (inst) inst.dispose();
  });
}
